/**
 * FlowStateSkillEffect — Specter wraith-path passive: momentum speed bonus.
 *
 * Each consecutive second of unbroken movement — walking, running, or stealthed
 * movement — increases movement speed by 3%, stacking to 30%. Any full stop or
 * non-movement action (attacking) resets the stack.
 *
 * Always on, no slot cost. The bonus is applied through the player's
 * movementSpeedMultiplier property.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-drift-path-proposal.md § Flow State
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface FlowStateDeps {
  readonly player: {
    /** Whether the player is currently moving (has nonzero velocity). */
    isMoving(): boolean;
    /** Whether the player is currently attacking (resets flow). */
    isAttacking(): boolean;
    /** Set the movement speed multiplier on the player entity. */
    setMovementSpeedMultiplier(value: number): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface FlowStateConfig {
  /** Speed bonus per second of movement (fraction). */
  readonly speedPerSecond: number;
  /** Maximum speed bonus (fraction). */
  readonly maxSpeedBonus: number;
}

const DEFAULT_CONFIG: FlowStateConfig = {
  speedPerSecond: 0.03,
  maxSpeedBonus: 0.3,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class FlowStateSkillEffect implements SkillEffect {
  readonly skillId = 'flow_state';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;
  readonly staminaCost: number = 0;

  private readonly deps: FlowStateDeps;
  private readonly config: FlowStateConfig;

  /** Accumulated movement time in ms. */
  private _movementTimeMs = 0;
  /** Current speed bonus fraction (0 to maxSpeedBonus). */
  private _currentBonus = 0;

  constructor(deps: FlowStateDeps, config: Partial<FlowStateConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  /** Current speed bonus as a fraction (e.g. 0.09 for 9%). */
  get currentBonus(): number { return this._currentBonus; }
  /** Accumulated consecutive movement time in ms. */
  get movementTimeMs(): number { return this._movementTimeMs; }

  // --- SkillEffect interface ---

  canActivate(): boolean { return false; }
  activate(): boolean { return false; }
  deactivate(): void {}
  isActive(): boolean { return false; }

  update(delta: number): void {
    const moving = this.deps.player.isMoving();
    const attacking = this.deps.player.isAttacking();

    if (moving && !attacking) {
      // Accumulate movement time
      this._movementTimeMs += delta;

      // Calculate bonus: speedPerSecond * seconds of movement, capped
      const seconds = this._movementTimeMs / 1000;
      this._currentBonus = Math.min(
        this.config.speedPerSecond * seconds,
        this.config.maxSpeedBonus,
      );
    } else {
      // Full stop or attacking — reset
      this._movementTimeMs = 0;
      this._currentBonus = 0;
    }

    // Apply multiplier (1.0 = no bonus)
    this.deps.player.setMovementSpeedMultiplier(1 + this._currentBonus);
  }

  destroy(): void {
    this._movementTimeMs = 0;
    this._currentBonus = 0;
    // Reset multiplier to neutral on cleanup
    this.deps.player.setMovementSpeedMultiplier(1);
  }
}
